import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
    ArrowLeft,
    ChevronRight,
    ChevronLeft,
    Check,
    CheckCircle2,
    AlertCircle,
    Upload,
    X,
    Plus,
    Loader2,
    Globe,
    Info,
    Save,
    Star,
    Video,
} from "lucide-react";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import {
    API,
    LANGS,
    LANG_NAME,
    emptyML,
    toML,
    fillML,
    asArray,
    mlText,
    num,
    fmtMoney,
    errMsg,
    splitList,
    STOCK_STATUS,
    APPROVAL_STYLE,
    DEFAULT_ORIGIN,
    DEFAULT_RETURN_POLICY,
    DEFAULT_WARRANTY,
    DEFAULT_TRADE_INFO,
} from "./Productshared";

/* ------------------------------------------------------------------ */
/*  config                                                             */
/* ------------------------------------------------------------------ */
const TABS = ["Vital Info", "Images", "Offer", "Description", "More Details", "Delivery"];
const MAX_IMAGES = 9;
const ML_FIELDS = ["name", "description", "material", "finishType", "careInstruction", "measurement"];

/** which form fields live on which tab (drives the tab check / error badges) */
const TAB_FIELDS = {
    "Vital Info": ["name", "sku", "category", "subCategory", "ratings"],
    Images: ["images"],
    Offer: ["previous_price", "price", "gstPercentage", "shippingCharge"],
    Description: ["description", "features"],
    "More Details": ["material", "finishType", "careInstruction", "measurement", "productType", "productVideo"],
    Delivery: [],
};

/** required items shown in the "Listing completeness" panel */
const REQUIRED = [
    ["name", "Product name", "Vital Info"],
    ["sku", "SKU", "Vital Info"],
    ["category", "Category", "Vital Info"],
    ["subCategory", "Sub category", "Vital Info"],
    ["ratings", "Rating", "Vital Info"],
    ["images", "Main image", "Images"],
    ["previous_price", "MRP", "Offer"],
    ["price", "Selling price", "Offer"],
    ["description", "Description", "Description"],
    ["material", "Material", "More Details"],
    ["finishType", "Finish type", "More Details"],
    ["careInstruction", "Care instructions", "More Details"],
    ["measurement", "Measurement", "More Details"],
    ["productType", "Product type", "More Details"],
];

const blankForm = () => ({
    name: emptyML(),
    description: emptyML(),
    material: emptyML(),
    finishType: emptyML(),
    origin: { ...DEFAULT_ORIGIN },
    careInstruction: emptyML(),
    return_policy: { ...DEFAULT_RETURN_POLICY },
    measurement: emptyML(),
    productType: { en: "Handicraft", hi: "", es: "", zh: "" },
    sku: "",
    category: "",
    subCategory: "",
    height: "",
    weight: "",
    brand: "",
    ratings: "",
    price: "",
    previous_price: "",
    gstPercentage: "18",
    shippingCharge: "150",
    features: [],
    isBestProduct: false,
    is_special_product: false,
    productVideo: "",
    delivery: {
        useCustomZone: false,
        allowedStates: "",
        allowedCities: "",
        allowedPincodes: "",
        blockedStates: "",
        blockedCities: "",
        blockedPincodes: "",
        notDeliverableMessage: "This product is not deliverable at your location.",
    },
});

const fromProduct = (p) => {
    const d = p.deliveryRestrictions || {};
    const join = (a) => (Array.isArray(a) ? a.join(", ") : "");
    return {
        name: toML(p.name),
        description: toML(p.description),
        material: toML(p.material),
        finishType: toML(p.finishType),
        origin: toML(p.origin),
        careInstruction: toML(p.careInstruction),
        return_policy: toML(p.return_policy),
        measurement: toML(p.measurement),
        productType: toML(p.productType),
        sku: p.sku || "",
        category: p.category?._id || p.category || "",
        subCategory: p.subCategory?._id || p.subCategory || "",
        height: p.height || "",
        weight: p.weight || "",
        brand: p.brand || "",
        ratings: p.ratings ? String(p.ratings) : "",
        price: p.price != null ? String(p.price) : "",
        previous_price: p.previous_price != null ? String(p.previous_price) : "",
        gstPercentage: p.gstPercentage != null ? String(p.gstPercentage) : "18",
        shippingCharge: p.shippingCharge != null ? String(p.shippingCharge) : "150",
        features: Array.isArray(p.features) ? p.features.map(toML) : [],
        isBestProduct: Boolean(p.isBestProduct),
        is_special_product: Boolean(p.is_special_product),
        productVideo: p.productVideo || "",
        delivery: {
            useCustomZone: Boolean(d.useCustomZone),
            allowedStates: join(d.allowedStates),
            allowedCities: join(d.allowedCities),
            allowedPincodes: join(d.allowedPincodes),
            blockedStates: join(d.blockedStates),
            blockedCities: join(d.blockedCities),
            blockedPincodes: join(d.blockedPincodes),
            notDeliverableMessage: d.notDeliverableMessage || "This product is not deliverable at your location.",
        },
    };
};

/* ------------------------------------------------------------------ */
/*  small UI pieces (top-level on purpose: inner components would      */
/*  remount on every keystroke and steal focus)                        */
/* ------------------------------------------------------------------ */
const cardCls = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";
const inputCls = (bad) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 ${bad ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:ring-[#d4af37]/50 focus:border-[#d4af37]"
    }`;

const Section = ({ title, subtitle, children }) => (
    <section className={cardCls}>
        <header className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl">
            <h2 className="text-[15px] font-bold text-slate-800">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </header>
        {children}
    </section>
);

const FieldRow = ({ label, required, help, error, htmlFor, children }) => (
    <div className="grid grid-cols-1 md:grid-cols-[210px_1fr] gap-x-6 gap-y-1.5 px-5 py-4 border-b border-slate-100 last:border-b-0">
        <div>
            <label htmlFor={htmlFor} className="text-sm font-semibold text-slate-800">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {help && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{help}</p>}
        </div>
        <div className="min-w-0">
            {children}
            {error && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" role="alert">
                    <AlertCircle size={12} /> {error}
                </p>
            )}
        </div>
    </div>
);

/** one multilingual value, edited in the language chosen in the header */
const MLField = ({ value, lang, onChange, textarea, rows = 6, placeholder, invalid, id, maxLength }) => {
    const common = {
        id,
        value: value[lang] || "",
        onChange: (e) => onChange(e.target.value),
        placeholder: lang === "en" ? placeholder : "Left empty = English text is used",
        maxLength,
        "aria-invalid": invalid || undefined,
        className: `${inputCls(invalid)} pr-11`,
    };
    return (
        <div>
            <div className="relative">
                {textarea ? <textarea rows={rows} {...common} /> : <input type="text" {...common} />}
                <span className="absolute right-2.5 top-2 text-[10px] font-bold tracking-wide text-slate-400 bg-white pl-1">
                    {lang.toUpperCase()}
                </span>
            </div>
            {lang !== "en" && value.en && (
                <p className="mt-1 text-xs text-slate-500">
                    English: <span className="text-slate-700">{value.en}</span>
                </p>
            )}
            {textarea && maxLength && (
                <p className="mt-1 text-xs text-slate-400 text-right">
                    {(value[lang] || "").length}/{maxLength}
                </p>
            )}
        </div>
    );
};

const ListField = ({ label, value, onChange, placeholder, tone }) => {
    const items = splitList(value);
    const chip = tone === "red" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700";
    return (
        <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={inputCls(false)}
            />
            {items.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {items.map((it, i) => (
                        <span key={`${it}-${i}`} className={`text-[11px] px-2 py-0.5 rounded-full ${chip}`}>
                            {it}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const Toggle = ({ checked, onChange, label, help, id }) => (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-[#e47911]" />
        <span>
            <span className="block text-sm font-medium text-slate-800">{label}</span>
            {help && <span className="block text-xs text-slate-500 mt-0.5">{help}</span>}
        </span>
    </label>
);

/* ------------------------------------------------------------------ */
/*  page                                                               */
/* ------------------------------------------------------------------ */
const ProductForm = ({ mode = "add" }) => {
    const isEdit = mode === "edit";
    const { id } = useParams();
    const navigate = useNavigate();

    const [form, setForm] = useState(blankForm);
    const [images, setImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allSubs, setAllSubs] = useState([]);
    const [original, setOriginal] = useState(null);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(0);
    const [videoUploading, setVideoUploading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [lang, setLang] = useState("en");
    const [tab, setTab] = useState(TABS[0]);

    /* ---------- load ---------- */
    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const reqs = [
                axios.get(`${API}/categories/get-all-categories`),
                axios.get(`${API}/sub-categories/get-all-sub-categories`),
            ];
            if (isEdit) reqs.push(axios.get(`${API}/products/get-product-by-id/${id}`));
            const [cats, subs, prod] = await Promise.all(reqs);
            setCategories(asArray(cats.data, "categories"));
            setAllSubs(asArray(subs.data, "subCategories"));

            if (isEdit) {
                const p = prod.data?.product || prod.data;
                if (!p?._id) throw new Error("Product not found");
                setOriginal(p);
                setForm(fromProduct(p));
                setImages(Array.isArray(p.images) ? p.images : []);
            }
        } catch (err) {
            console.error(err);
            setLoadError(errMsg(err, "Failed to load the data for this page."));
        } finally {
            setLoading(false);
        }
    }, [id, isEdit]);

    useEffect(() => {
        load();
    }, [load]);

    /* warn before closing the tab with unsaved changes */
    useEffect(() => {
        if (!dirty || saving) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty, saving]);

    /* ---------- setters ---------- */
    const patch = (p) => {
        setForm((f) => ({ ...f, ...p }));
        setDirty(true);
    };
    const setML = (name, value) => {
        setForm((f) => ({ ...f, [name]: { ...f[name], [lang]: value } }));
        setDirty(true);
    };
    const setDelivery = (name, value) => {
        setForm((f) => ({ ...f, delivery: { ...f.delivery, [name]: value } }));
        setDirty(true);
    };
    const changeCategory = (value) => patch({ category: value, subCategory: "" });

    const subCategories = useMemo(
        () => allSubs.filter((s) => (s.category?._id || s.category) === form.category),
        [allSubs, form.category]
    );

    /* features */
    const addFeature = () => {
        if (form.features.length >= 10) return toast.error("You can add up to 10 features");
        patch({ features: [...form.features, emptyML()] });
    };
    const setFeature = (i, value) => {
        setForm((f) => ({
            ...f,
            features: f.features.map((ft, idx) => (idx === i ? { ...ft, [lang]: value } : ft)),
        }));
        setDirty(true);
    };
    const removeFeature = (i) => patch({ features: form.features.filter((_, idx) => idx !== i) });

    /* ---------- images ---------- */
    const uploadFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        const imagesOnly = files.filter((f) => f.type.startsWith("image/"));
        if (!imagesOnly.length) {
            if (files.length) toast.error("Please choose image files (JPG, PNG, WEBP)");
            return;
        }
        const tooBig = imagesOnly.filter((f) => f.size > 10 * 1024 * 1024);
        if (tooBig.length) toast.error(`${tooBig.length} image(s) are over 10 MB and were skipped`);
        const ok = imagesOnly.filter((f) => f.size <= 10 * 1024 * 1024);

        const room = MAX_IMAGES - images.length - uploading;
        if (room <= 0) return toast.error(`You can add up to ${MAX_IMAGES} images`);
        const take = ok.slice(0, room);
        if (ok.length > room) toast(`Only ${room} more image(s) fit. The rest were skipped.`);
        if (!take.length) return;

        setUploading((u) => u + take.length);
        const results = await Promise.allSettled(take.map((f) => uploadToCloudinary(f)));
        const urls = results.filter((r) => r.status === "fulfilled" && r.value?.secure_url).map((r) => r.value.secure_url);
        const failed = take.length - urls.length;

        if (urls.length) {
            setImages((prev) => [...prev, ...urls].slice(0, MAX_IMAGES));
            setDirty(true);
        }
        if (failed) toast.error(`${failed} image(s) failed to upload. Please try again.`);
        setUploading((u) => u - take.length);
    };

    const removeImage = (i) => {
        setImages((prev) => prev.filter((_, idx) => idx !== i));
        setDirty(true);
    };
    const makeMain = (i) => {
        setImages((prev) => [prev[i], ...prev.filter((_, idx) => idx !== i)]);
        setDirty(true);
    };

    const uploadVideo = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("video/")) return toast.error("Please choose a video file");
        try {
            setVideoUploading(true);
            const body = new FormData();
            body.append("file", file);
            body.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
            const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/video/upload`, {
                method: "POST",
                body,
            });
            const data = await res.json();
            if (!data.secure_url) throw new Error(data.error?.message || "Upload failed");
            patch({ productVideo: data.secure_url });
            toast.success("Video uploaded");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Video upload failed");
        } finally {
            setVideoUploading(false);
        }
    };

    /* ---------- validation ---------- */
    const errors = useMemo(() => {
        const e = {};
        const need = "Required";
        if (!form.name.en.trim()) e.name = "Enter the product name in English";
        if (!form.sku.trim()) e.sku = need;
        if (!form.category) e.category = "Choose a category";
        if (!form.subCategory) e.subCategory = "Choose a sub category";
        const r = Number(form.ratings);
        if (form.ratings === "" || !(r >= 1 && r <= 5)) e.ratings = "Enter a rating from 1 to 5";
        if (images.length === 0) e.images = "Add at least one image. The first image is the main image.";

        if (form.previous_price === "" || !(Number(form.previous_price) > 0)) e.previous_price = "Enter the MRP";
        if (form.price === "" || !(Number(form.price) > 0)) e.price = "Enter the selling price";
        const gst = Number(form.gstPercentage);
        if (form.gstPercentage === "" || !(gst >= 0 && gst <= 100)) e.gstPercentage = "Between 0 and 100";
        if (form.shippingCharge === "" || !(Number(form.shippingCharge) >= 0)) e.shippingCharge = "0 or more";

        if (!form.description.en.trim()) e.description = "Enter the description in English";
        const badFeature = form.features.findIndex((f) => !f.en.trim() && (f.hi || f.es || f.zh).trim());
        if (badFeature >= 0) e.features = `Feature ${badFeature + 1}: add the English text`;

        // The catalogue schema stores these with a required English value
        if (!form.material.en.trim()) e.material = "Required by the catalogue";
        if (!form.finishType.en.trim()) e.finishType = "Required by the catalogue";
        if (!form.careInstruction.en.trim()) e.careInstruction = "Required by the catalogue";
        if (!form.measurement.en.trim()) e.measurement = "Required by the catalogue";
        if (!form.productType.en.trim()) e.productType = "Required by the catalogue";
        if (form.is_special_product && !form.productVideo) e.productVideo = "A video is required for a special product";
        return e;
    }, [form, images]);

    const tabState = (t) => {
        const fields = TAB_FIELDS[t];
        if (!fields.length) return null;
        const bad = fields.some((k) => errors[k]);
        if (submitted && bad) return "error";
        return bad ? null : "ok";
    };

    const done = REQUIRED.filter(([k]) => !errors[k]).length;
    const pct = Math.round((done / REQUIRED.length) * 100);

    const translations = useMemo(
        () =>
            LANGS.map((l) => ({
                ...l,
                filled: ML_FIELDS.filter((f) => (form[f][l.code] || "").trim()).length,
            })),
        [form]
    );

    /* ---------- discount preview ---------- */
    const mrp = num(form.previous_price);
    const sell = num(form.price);
    const discount = mrp > 0 && sell > 0 ? ((mrp - sell) / mrp) * 100 : null;

    /* ---------- save ---------- */
    const buildPayload = () => {
        const d = form.delivery;
        const payload = {
            sku: form.sku.trim(),
            name: fillML(form.name),
            description: fillML(form.description),
            material: fillML(form.material),
            finishType: fillML(form.finishType),
            origin: fillML(form.origin),
            careInstruction: fillML(form.careInstruction),
            return_policy: fillML(form.return_policy),
            measurement: fillML(form.measurement),
            productType: fillML(form.productType),
            category: form.category,
            subCategory: form.subCategory,
            height: form.height.trim(),
            weight: form.weight.trim(),
            brand: form.brand.trim(),
            ratings: Number(form.ratings),
            price: Number(form.price),
            previous_price: Number(form.previous_price),
            gstPercentage: Number(form.gstPercentage),
            shippingCharge: Number(form.shippingCharge),
            features: form.features.filter((f) => f.en.trim()).map(fillML),
            images,
            isBestProduct: form.isBestProduct,
            is_special_product: form.is_special_product,
            // clearing the video is how the server turns "special" off
            productVideo: form.is_special_product ? form.productVideo : "",
            deliveryRestrictions: {
                useCustomZone: d.useCustomZone,
                notDeliverableMessage: d.notDeliverableMessage,
                allowedStates: splitList(d.allowedStates),
                allowedCities: splitList(d.allowedCities),
                allowedPincodes: splitList(d.allowedPincodes),
                blockedStates: splitList(d.blockedStates),
                blockedCities: splitList(d.blockedCities),
                blockedPincodes: splitList(d.blockedPincodes),
            },
        };

        if (!isEdit) {
            Object.assign(payload, {
                stock_status: "active",
                uploadedBy: "admin",
                supplier: null,
                warranty: DEFAULT_WARRANTY,
                tradeInfo: DEFAULT_TRADE_INFO,
            });
        }
        // Edit deliberately does NOT send stock_status: the old form reset every edited product to "active".
        return payload;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (uploading > 0 || videoUploading) return toast.error("Please wait for the uploads to finish");

        setSubmitted(true);
        const keys = Object.keys(errors);
        if (keys.length) {
            const firstTab = TABS.find((t) => TAB_FIELDS[t].some((k) => errors[k]));
            if (firstTab) setTab(firstTab);
            toast.error(`Please fix ${keys.length} field${keys.length > 1 ? "s" : ""} before saving`);
            return;
        }

        setSaving(true);
        try {
            const payload = buildPayload();
            if (isEdit) {
                await axios.put(`${API}/products/update-product/${id}`, payload);
                toast.success("Product updated");
            } else {
                await axios.post(`${API}/products/create-product`, payload);
                toast.success("Product added");
            }
            setDirty(false);
            navigate("/admin-products");
        } catch (err) {
            console.error(err);
            toast.error(errMsg(err, isEdit ? "Error updating product" : "Error creating product"));
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => {
        if (dirty && !window.confirm("You have unsaved changes. Leave this page?")) return;
        navigate("/admin-products");
    };

    /* ---------- loading / error ---------- */
    if (loading) {
        return (
            <div className="min-h-screen bg-[#F3F6FB] p-6 animate-pulse">
                <div className="max-w-[1280px] mx-auto space-y-5">
                    <div className="h-4 w-56 bg-slate-200 rounded" />
                    <div className="h-10 w-72 bg-slate-200 rounded" />
                    <div className="h-12 bg-white border border-slate-200 rounded-xl" />
                    <div className="grid lg:grid-cols-[1fr_320px] gap-5">
                        <div className="h-[480px] bg-white border border-slate-200 rounded-xl" />
                        <div className="h-[320px] bg-white border border-slate-200 rounded-xl" />
                    </div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="min-h-screen bg-[#F3F6FB] flex flex-col items-center justify-center p-6 text-center">
                <AlertCircle size={44} className="text-slate-300" />
                <p className="mt-3 text-lg font-semibold text-slate-800">Couldn't open this page</p>
                <p className="text-sm text-slate-500 mt-1 max-w-sm">{loadError}</p>
                <div className="flex gap-3 mt-5">
                    <button onClick={load} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#131921] text-white hover:bg-[#1f2937]">
                        Try again
                    </button>
                    <button
                        onClick={() => navigate("/admin-products")}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 hover:bg-slate-50"
                    >
                        Back to products
                    </button>
                </div>
            </div>
        );
    }

    const tabIndex = TABS.indexOf(tab);
    const stockInfo = original ? STOCK_STATUS[original.stock_status] : null;
    const showErr = (k) => (submitted ? errors[k] : undefined);

    return (
        <div className="min-h-screen bg-[#F3F6FB] p-4 md:p-6 font-sans">
            <form
                onSubmit={handleSubmit}
                noValidate
                onKeyDown={(e) => {
                    // Enter in a text box must not save a half-filled multi-tab form
                    if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") e.preventDefault();
                }}
                className="w-full mx-auto space-y-5"
            >
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
                    <button type="button" onClick={goBack} className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:underline">
                        <ArrowLeft size={15} /> Products
                    </button>
                    <ChevronRight size={14} />
                    <span className="text-slate-700 font-medium">{isEdit ? "Edit product" : "Add a product"}</span>
                </nav>

                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="text-[28px] leading-8 font-extrabold text-slate-900 tracking-tight">
                            {isEdit ? "Edit product" : "Add a product"}
                        </h1>
                        {isEdit && original ? (
                            <p className="flex flex-wrap items-center gap-2 mt-2 text-sm text-slate-500">
                                <span className="font-mono text-[13px]">{original.bhkg_id}</span>
                                <span>·</span>
                                <span>SKU {original.sku}</span>
                                {stockInfo && <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${stockInfo.pill}`}>{stockInfo.label}</span>}
                                {original.approval_status && (
                                    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${APPROVAL_STYLE[original.approval_status] || APPROVAL_STYLE.Draft}`}>
                                        {original.approval_status}
                                    </span>
                                )}
                            </p>
                        ) : (
                            <p className="text-sm text-slate-500 mt-1.5">Fill in the tabs below. Fields marked * are required.</p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                            <Globe size={15} className="text-slate-400" />
                            <span className="hidden sm:inline">Language</span>
                            <select
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                                aria-label="Editing language"
                                className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50"
                            >
                                {LANGS.map((l) => (
                                    <option key={l.code} value={l.code}>
                                        {l.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            onClick={goBack}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-slate-900 border border-[#d4af37] bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] hover:from-[#f5d78e] hover:to-[#eeb933] shadow-sm disabled:opacity-60"
                        >
                            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {saving ? "Saving…" : "Save and finish"}
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)] overflow-x-auto">
                    <div className="flex min-w-max" role="tablist">
                        {TABS.map((t) => {
                            const active = t === tab;
                            const st = tabState(t);
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => setTab(t)}
                                    className={`flex items-center gap-2 px-5 py-3.5 text-sm whitespace-nowrap border-b-[3px] transition-colors ${active
                                        ? "border-[#e47911] text-slate-900 font-bold bg-amber-50/40"
                                        : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                        }`}
                                >
                                    {t}
                                    {st === "ok" && <CheckCircle2 size={15} className="text-emerald-500" />}
                                    {st === "error" && <AlertCircle size={15} className="text-red-500" />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
                    {/* ---------------- CONTENT ---------------- */}
                    <div className="space-y-5 min-w-0">
                        {/* ===== Vital Info ===== */}
                        {tab === "Vital Info" && (
                            <Section title="Vital info" subtitle="The basics that identify this product.">
                                <FieldRow label="Product name" required htmlFor="f-name" error={showErr("name")} help="Shown to customers. Keep it clear and specific.">
                                    <MLField id="f-name" value={form.name} lang={lang} onChange={(v) => setML("name", v)} placeholder="e.g. Handwoven Jute Basket Set (3 pieces)" invalid={Boolean(showErr("name"))} maxLength={200} />
                                </FieldRow>
                                <FieldRow label="SKU" required htmlFor="f-sku" error={showErr("sku")} help="Your unique stock code.">
                                    <input id="f-sku" type="text" value={form.sku} onChange={(e) => patch({ sku: e.target.value })} placeholder="e.g. HAND001" className={inputCls(showErr("sku"))} />
                                </FieldRow>
                                <FieldRow label="Category" required htmlFor="f-cat" error={showErr("category")}>
                                    <select id="f-cat" value={form.category} onChange={(e) => changeCategory(e.target.value)} className={inputCls(showErr("category"))}>
                                        <option value="">Select category</option>
                                        {categories.map((c) => (
                                            <option key={c._id} value={c._id}>
                                                {mlText(c.name)}
                                            </option>
                                        ))}
                                    </select>
                                </FieldRow>
                                <FieldRow label="Sub category" required htmlFor="f-sub" error={showErr("subCategory")}>
                                    <select
                                        id="f-sub"
                                        value={form.subCategory}
                                        onChange={(e) => patch({ subCategory: e.target.value })}
                                        disabled={!form.category || subCategories.length === 0}
                                        className={`${inputCls(showErr("subCategory"))} disabled:bg-slate-100 disabled:cursor-not-allowed`}
                                    >
                                        <option value="">
                                            {!form.category ? "Select a category first" : subCategories.length === 0 ? "No sub categories available" : "Select sub category"}
                                        </option>
                                        {subCategories.map((s) => (
                                            <option key={s._id} value={s._id}>
                                                {mlText(s.name)}
                                            </option>
                                        ))}
                                    </select>
                                </FieldRow>
                                <FieldRow label="Rating" required htmlFor="f-rating" error={showErr("ratings")} help="Between 1 and 5.">
                                    <div className="relative max-w-[200px]">
                                        <Star size={15} className="absolute left-3 top-2.5 text-amber-500 pointer-events-none" />
                                        <input id="f-rating" type="number" min="1" max="5" step="0.1" value={form.ratings} onChange={(e) => patch({ ratings: e.target.value })} placeholder="4.5" className={`${inputCls(showErr("ratings"))} pl-9`} />
                                    </div>
                                </FieldRow>
                            </Section>
                        )}

                        {/* ===== Images ===== */}
                        {tab === "Images" && (
                            <Section title="Images" subtitle={`Up to ${MAX_IMAGES} images. The first one is the main image shown in listings.`}>
                                <div className="p-5">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {images.map((src, i) => (
                                            <div key={`${src}-${i}`} className="group relative aspect-square rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                                                <img src={src} alt={i === 0 ? "Main" : `Image ${i + 1}`} className="w-full h-full object-contain" />
                                                <span className={`absolute left-2 top-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${i === 0 ? "bg-[#131921] text-white" : "bg-white/90 text-slate-600 border border-slate-200"}`}>
                                                    {i === 0 ? "MAIN" : `IMAGE ${i + 1}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(i)}
                                                    aria-label={`Remove image ${i + 1}`}
                                                    className="absolute right-2 top-2 p-1 rounded-full bg-white/95 text-red-500 border border-slate-200 shadow-sm hover:bg-red-50"
                                                >
                                                    <X size={14} />
                                                </button>
                                                {i !== 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => makeMain(i)}
                                                        className="absolute inset-x-2 bottom-2 text-[11px] font-medium py-1 rounded-md bg-white/95 border border-slate-200 text-slate-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                                    >
                                                        Make main
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        {Array.from({ length: uploading }).map((_, i) => (
                                            <div key={`up-${i}`} className="aspect-square rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
                                                <Loader2 size={22} className="animate-spin text-slate-400" />
                                            </div>
                                        ))}

                                        {images.length + uploading < MAX_IMAGES && (
                                            <label
                                                htmlFor="f-images"
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    uploadFiles(e.dataTransfer.files);
                                                }}
                                                className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-[#d4af37] hover:bg-amber-50/40 flex flex-col items-center justify-center gap-1.5 text-center cursor-pointer transition-colors px-2"
                                            >
                                                {images.length === 0 ? <Upload size={22} className="text-slate-400" /> : <Plus size={22} className="text-slate-400" />}
                                                <span className="text-xs font-medium text-slate-600">{images.length === 0 ? "Add main image" : "Add images"}</span>
                                                <span className="text-[11px] text-slate-400">or drop files here</span>
                                            </label>
                                        )}
                                    </div>
                                    <input
                                        id="f-images"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                            uploadFiles(e.target.files);
                                            e.target.value = "";
                                        }}
                                    />
                                    {showErr("images") && (
                                        <p className="mt-3 text-xs text-red-600 flex items-center gap-1" role="alert">
                                            <AlertCircle size={12} /> {errors.images}
                                        </p>
                                    )}
                                    <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                                        Tips: use a plain light background for the main image, show the product from several angles, and keep each file under 10 MB.
                                    </p>
                                </div>
                            </Section>
                        )}

                        {/* ===== Offer ===== */}
                        {tab === "Offer" && (
                            <>
                                <Section title="Pricing" subtitle="All prices are in Indian rupees (₹).">
                                    <FieldRow label="Base price (MRP)" required htmlFor="f-mrp" error={showErr("previous_price")}>
                                        <div className="relative max-w-[240px]">
                                            <span className="absolute left-3 top-2 text-slate-400">₹</span>
                                            <input id="f-mrp" type="number" min="0" step="0.01" value={form.previous_price} onChange={(e) => patch({ previous_price: e.target.value })} placeholder="0.00" className={`${inputCls(showErr("previous_price"))} pl-7`} />
                                        </div>
                                    </FieldRow>
                                    <FieldRow label="Selling price" required htmlFor="f-price" error={showErr("price")}>
                                        <div className="relative max-w-[240px]">
                                            <span className="absolute left-3 top-2 text-slate-400">₹</span>
                                            <input id="f-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => patch({ price: e.target.value })} placeholder="0.00" className={`${inputCls(showErr("price"))} pl-7`} />
                                        </div>
                                        {discount !== null && discount > 0 && (
                                            <p className="mt-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-block">
                                                <b>{discount.toFixed(1)}% off</b> · customers save {fmtMoney(mrp - sell)}
                                            </p>
                                        )}
                                        {discount !== null && discount < 0 && (
                                            <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
                                                The selling price is higher than the MRP. Please double check.
                                            </p>
                                        )}
                                    </FieldRow>
                                </Section>

                                <Section title="Tax & shipping">
                                    <FieldRow label="GST percentage" required htmlFor="f-gst" error={showErr("gstPercentage")}>
                                        <div className="relative max-w-[200px]">
                                            <input id="f-gst" type="number" min="0" max="100" step="0.01" value={form.gstPercentage} onChange={(e) => patch({ gstPercentage: e.target.value })} className={`${inputCls(showErr("gstPercentage"))} pr-8`} />
                                            <span className="absolute right-3 top-2 text-slate-400">%</span>
                                        </div>
                                    </FieldRow>
                                    <FieldRow label="Shipping charge" required htmlFor="f-ship" error={showErr("shippingCharge")} help="Per unit.">
                                        <div className="relative max-w-[200px]">
                                            <span className="absolute left-3 top-2 text-slate-400">₹</span>
                                            <input id="f-ship" type="number" min="0" step="0.01" value={form.shippingCharge} onChange={(e) => patch({ shippingCharge: e.target.value })} className={`${inputCls(showErr("shippingCharge"))} pl-7`} />
                                        </div>
                                    </FieldRow>
                                </Section>

                                <Section title="Inventory">
                                    <div className="px-5 py-4 text-sm text-slate-600">
                                        {isEdit && original ? (
                                            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-3">
                                                <span>
                                                    In stock: <b className="text-slate-900">{original.stock ?? 0}</b>
                                                </span>
                                                <span>
                                                    Available: <b className="text-slate-900">{original.inventory?.available ?? 0}</b>
                                                </span>
                                                <span>
                                                    Reserved: <b className="text-slate-900">{original.inventory?.reserved ?? 0}</b>
                                                </span>
                                            </div>
                                        ) : null}
                                        <p className="flex gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5">
                                            <Info size={14} className="shrink-0 mt-0.5" />
                                            Stock is not entered here. It is added when a purchase is marked as <b>Received</b> and used up by customer orders.
                                        </p>
                                    </div>
                                </Section>
                            </>
                        )}

                        {/* ===== Description ===== */}
                        {tab === "Description" && (
                            <>
                                <Section title="Product description">
                                    <FieldRow label="Description" required htmlFor="f-desc" error={showErr("description")} help="Tell the story of the product, the artisan and the craft.">
                                        <MLField id="f-desc" textarea rows={9} maxLength={2000} value={form.description} lang={lang} onChange={(v) => setML("description", v)} placeholder="Describe the product in detail…" invalid={Boolean(showErr("description"))} />
                                    </FieldRow>
                                </Section>

                                <Section title="Key product features" subtitle="Short bullet points shown under “About this item”. Up to 10.">
                                    <div className="p-5 space-y-3">
                                        {form.features.length === 0 && <p className="text-sm text-slate-500">No features yet.</p>}
                                        {form.features.map((f, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <span className="mt-2 text-xs font-semibold text-slate-400 w-5 text-right">{i + 1}.</span>
                                                <div className="flex-1 min-w-0">
                                                    <MLField value={f} lang={lang} onChange={(v) => setFeature(i, v)} placeholder="e.g. Handwoven by artisans in Odisha" maxLength={200} />
                                                </div>
                                                <button type="button" onClick={() => removeFeature(i)} aria-label={`Remove feature ${i + 1}`} className="mt-1.5 p-1.5 rounded-md text-red-500 hover:bg-red-50">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        {showErr("features") && (
                                            <p className="text-xs text-red-600 flex items-center gap-1" role="alert">
                                                <AlertCircle size={12} /> {errors.features}
                                            </p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={addFeature}
                                            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                                        >
                                            <Plus size={16} /> Add feature
                                        </button>
                                    </div>
                                </Section>
                            </>
                        )}

                        {/* ===== More Details ===== */}
                        {tab === "More Details" && (
                            <>
                                <Section title="Specifications">
                                    <FieldRow label="Brand" htmlFor="f-brand">
                                        <input id="f-brand" type="text" value={form.brand} onChange={(e) => patch({ brand: e.target.value })} placeholder="e.g. BHK Handicrafts" className={inputCls(false)} />
                                    </FieldRow>
                                    <FieldRow label="Product type" required htmlFor="f-type" error={showErr("productType")}>
                                        <MLField id="f-type" value={form.productType} lang={lang} onChange={(v) => setML("productType", v)} placeholder="e.g. Handicraft" invalid={Boolean(showErr("productType"))} />
                                    </FieldRow>
                                    <FieldRow label="Material" required htmlFor="f-material" error={showErr("material")}>
                                        <MLField id="f-material" value={form.material} lang={lang} onChange={(v) => setML("material", v)} placeholder="e.g. Natural jute fibre with cotton thread" invalid={Boolean(showErr("material"))} />
                                    </FieldRow>
                                    <FieldRow label="Finish type" required htmlFor="f-finish" error={showErr("finishType")}>
                                        <MLField id="f-finish" value={form.finishType} lang={lang} onChange={(v) => setML("finishType", v)} placeholder="e.g. Natural jute finish" invalid={Boolean(showErr("finishType"))} />
                                    </FieldRow>
                                    <FieldRow label="Measurement" required htmlFor="f-measure" error={showErr("measurement")}>
                                        <MLField id="f-measure" value={form.measurement} lang={lang} onChange={(v) => setML("measurement", v)} placeholder='e.g. Set of 3: 6"H x 8"D, 10"H x 12"D' invalid={Boolean(showErr("measurement"))} />
                                    </FieldRow>
                                    <FieldRow label="Height" htmlFor="f-height">
                                        <input id="f-height" type="text" value={form.height} onChange={(e) => patch({ height: e.target.value })} placeholder="e.g. 12 inches" className={`${inputCls(false)} max-w-[260px]`} />
                                    </FieldRow>
                                    <FieldRow label="Weight" htmlFor="f-weight">
                                        <input id="f-weight" type="text" value={form.weight} onChange={(e) => patch({ weight: e.target.value })} placeholder="e.g. 4 kg" className={`${inputCls(false)} max-w-[260px]`} />
                                    </FieldRow>
                                    <FieldRow label="Origin" htmlFor="f-origin">
                                        <MLField id="f-origin" value={form.origin} lang={lang} onChange={(v) => setML("origin", v)} />
                                    </FieldRow>
                                </Section>

                                <Section title="Care & returns">
                                    <FieldRow label="Care instructions" required htmlFor="f-care" error={showErr("careInstruction")}>
                                        <MLField id="f-care" value={form.careInstruction} lang={lang} onChange={(v) => setML("careInstruction", v)} placeholder="e.g. Spot clean only. Keep away from moisture." invalid={Boolean(showErr("careInstruction"))} />
                                    </FieldRow>
                                    <FieldRow label="Return policy" htmlFor="f-return">
                                        <MLField id="f-return" value={form.return_policy} lang={lang} onChange={(v) => setML("return_policy", v)} />
                                    </FieldRow>
                                </Section>

                                <Section title="Merchandising">
                                    <div className="p-5 space-y-4">
                                        <Toggle id="f-best" checked={form.isBestProduct} onChange={(v) => patch({ isBestProduct: v })} label="Best product" help="Shown in the “Best sellers” sections." />
                                        <Toggle
                                            id="f-special"
                                            checked={form.is_special_product}
                                            onChange={(v) => patch(v ? { is_special_product: true } : { is_special_product: false, productVideo: "" })}
                                            label="Special product"
                                            help="Special products need a product video."
                                        />

                                        {form.is_special_product && (
                                            <div className="ml-7 border border-slate-200 rounded-lg p-4 bg-slate-50/60">
                                                <p className="text-sm font-semibold text-slate-800 mb-2">
                                                    Product video <span className="text-red-500">*</span>
                                                </p>
                                                {form.productVideo ? (
                                                    <div className="relative max-w-md">
                                                        <video src={form.productVideo} controls className="w-full max-h-64 rounded-lg border border-slate-200 bg-black" />
                                                        <button
                                                            type="button"
                                                            onClick={() => patch({ productVideo: "" })}
                                                            aria-label="Remove video"
                                                            className="absolute right-2 top-2 p-1.5 rounded-full bg-white/95 text-red-500 border border-slate-200 shadow-sm hover:bg-red-50"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <label
                                                        htmlFor="f-video"
                                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 cursor-pointer"
                                                    >
                                                        {videoUploading ? <Loader2 size={15} className="animate-spin" /> : <Video size={15} />}
                                                        {videoUploading ? "Uploading…" : "Upload video"}
                                                    </label>
                                                )}
                                                <input id="f-video" type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={videoUploading} />
                                                {showErr("productVideo") && (
                                                    <p className="mt-2 text-xs text-red-600 flex items-center gap-1" role="alert">
                                                        <AlertCircle size={12} /> {errors.productVideo}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Section>
                            </>
                        )}

                        {/* ===== Delivery ===== */}
                        {tab === "Delivery" && (
                            <Section title="Delivery restrictions" subtitle="Optional. Leave this off to use your global delivery zones.">
                                <div className="p-5 space-y-5">
                                    <Toggle
                                        id="f-zone"
                                        checked={form.delivery.useCustomZone}
                                        onChange={(v) => setDelivery("useCustomZone", v)}
                                        label="Override the global delivery zones for this product"
                                    />

                                    {form.delivery.useCustomZone && (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-3">
                                                    <div>
                                                        <h3 className="text-sm font-semibold text-emerald-800">Allow only</h3>
                                                        <p className="text-xs text-emerald-700 mt-0.5">If anything is filled, delivery is ONLY allowed to these places.</p>
                                                    </div>
                                                    <ListField label="Allowed states (comma separated)" value={form.delivery.allowedStates} onChange={(v) => setDelivery("allowedStates", v)} placeholder="e.g. Maharashtra, Delhi" />
                                                    <ListField label="Allowed cities" value={form.delivery.allowedCities} onChange={(v) => setDelivery("allowedCities", v)} />
                                                    <ListField label="Allowed pincodes" value={form.delivery.allowedPincodes} onChange={(v) => setDelivery("allowedPincodes", v)} />
                                                </div>
                                                <div className="p-4 rounded-lg border border-red-200 bg-red-50/50 space-y-3">
                                                    <div>
                                                        <h3 className="text-sm font-semibold text-red-800">Block</h3>
                                                        <p className="text-xs text-red-700 mt-0.5">These places will never get delivery for this product.</p>
                                                    </div>
                                                    <ListField tone="red" label="Blocked states" value={form.delivery.blockedStates} onChange={(v) => setDelivery("blockedStates", v)} />
                                                    <ListField tone="red" label="Blocked cities" value={form.delivery.blockedCities} onChange={(v) => setDelivery("blockedCities", v)} />
                                                    <ListField tone="red" label="Blocked pincodes" value={form.delivery.blockedPincodes} onChange={(v) => setDelivery("blockedPincodes", v)} />
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor="f-nd" className="block text-sm font-semibold text-slate-800 mb-1">
                                                    “Not deliverable” message
                                                </label>
                                                <input id="f-nd" type="text" value={form.delivery.notDeliverableMessage} onChange={(e) => setDelivery("notDeliverableMessage", e.target.value)} className={inputCls(false)} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Section>
                        )}

                        {/* Prev / next */}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => setTab(TABS[Math.max(0, tabIndex - 1)])}
                                disabled={tabIndex === 0}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={16} /> Previous
                            </button>
                            {tabIndex < TABS.length - 1 ? (
                                <button
                                    type="button"
                                    onClick={() => setTab(TABS[tabIndex + 1])}
                                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium text-slate-900 border border-[#d4af37] bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] hover:from-[#f5d78e] hover:to-[#eeb933] shadow-sm"
                                >
                                    Next <ChevronRight size={16} />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-slate-900 border border-[#d4af37] bg-gradient-to-b from-[#f7dfa5] to-[#f0c14b] hover:from-[#f5d78e] hover:to-[#eeb933] shadow-sm disabled:opacity-60"
                                >
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                    {saving ? "Saving…" : "Save and finish"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ---------------- SIDEBAR ---------------- */}
                    <aside className="space-y-5 lg:sticky lg:top-6">
                        <section className={cardCls}>
                            <header className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl flex items-center justify-between">
                                <h2 className="text-[15px] font-bold text-slate-800">Listing completeness</h2>
                                <span className={`text-sm font-bold ${pct === 100 ? "text-emerald-600" : "text-slate-700"}`}>{pct}%</span>
                            </header>
                            <div className="p-5">
                                <div className="h-2 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                    <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-[#e47911]"}`} style={{ width: `${pct}%` }} />
                                </div>
                                <ul className="mt-4 space-y-1.5">
                                    {REQUIRED.map(([key, label, t]) => {
                                        const ok = !errors[key];
                                        return (
                                            <li key={key}>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(t)}
                                                    className="w-full flex items-center gap-2 text-left text-[13px] rounded-md px-1.5 py-1 hover:bg-slate-50"
                                                >
                                                    {ok ? (
                                                        <span className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                                            <Check size={11} strokeWidth={3} />
                                                        </span>
                                                    ) : (
                                                        <span className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                                                    )}
                                                    <span className={ok ? "text-slate-500" : "text-slate-800 font-medium"}>{label}</span>
                                                    {!ok && <span className="ml-auto text-[11px] text-slate-400">{t}</span>}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </section>

                        <section className={cardCls}>
                            <header className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl">
                                <h2 className="text-[15px] font-bold text-slate-800">Translations</h2>
                            </header>
                            <div className="p-5 space-y-3">
                                {translations.map((l) => (
                                    <button
                                        key={l.code}
                                        type="button"
                                        onClick={() => setLang(l.code)}
                                        className={`w-full text-left rounded-md px-1.5 py-1 hover:bg-slate-50 ${lang === l.code ? "bg-amber-50/60" : ""}`}
                                    >
                                        <div className="flex items-center justify-between text-[13px]">
                                            <span className={lang === l.code ? "font-semibold text-slate-900" : "text-slate-700"}>{LANG_NAME[l.code]}</span>
                                            <span className="text-slate-500">
                                                {l.filled}/{ML_FIELDS.length}
                                            </span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                                            <div className="h-full bg-sky-500 rounded-full" style={{ width: `${(l.filled / ML_FIELDS.length) * 100}%` }} />
                                        </div>
                                    </button>
                                ))}
                                <p className="text-xs text-slate-500 leading-relaxed">Anything left empty in another language is filled with the English text when you save.</p>
                            </div>
                        </section>
                    </aside>
                </div>
            </form>
        </div>
    );
};

export default ProductForm;